import React, { useCallback, useEffect, useState } from "react";
import { ACCENT } from "../artistStudio/shared";
import { parentalApi } from "./parentalApi";

// Parental Control 13+: "ask my parent for more time or more money".
//   - A teen asks from the Family page, or from the lock screen when the day's time is used up (AskMoreTime).
//   - The parents are told by the server (in the app and by email) and answer under "Requests from your teens" (IncomingRequests).
//   - Approving adds the extra to today's time or this month's allowance. The server does all the checking; these screens only send the
//     question and show the answer.
const messageOf = (e, fallback = "Something went wrong. Please try again.") => e?.response?.data?.message || fallback;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const MINUTE_CHOICES = [15, 30, 60];

const STATUS_TEXT = { pending: "Waiting for your parent", approved: "Approved", declined: "Declined", cancelled: "Cancelled", expired: "Expired" };
const STATUS_COLOR = { pending: "text-amber-700", approved: "text-green-700", declined: "text-red-600", cancelled: "text-gray-500", expired: "text-gray-500" };
const sentAt = (v) => (v ? new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// ---- teen: ask for more time (used on the lock screen and the Family page) --------------------------------------------------------
export function AskMoreTime({ onSent, compact }) {
  const [minutes, setMinutes] = useState(15);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const send = async () => {
    setBusy(true); setError("");
    try { await parentalApi.askFor({ kind: "time", minutes }); setSent(true); if (onSent) onSent(); } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  if (sent) return <p className="mt-3 text-sm text-green-700" role="status" data-testid="ask-time-sent">Asked. Your parent or guardian has been told. This screen opens by itself when they say yes.</p>;
  return (
    <div className={compact ? "mt-4" : "mt-3"} data-testid="ask-time">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <label className="text-sm text-gray-700" htmlFor="ask-minutes">Ask for</label>
        <select id="ask-minutes" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} disabled={busy} className="rounded-lg border border-gray-300 px-2 py-1 text-sm">
          {MINUTE_CHOICES.map((m) => <option key={m} value={m}>{m === 60 ? "1 hour" : `${m} minutes`}</option>)}
        </select>
        <span className="text-sm text-gray-700">more today</span>
        <button type="button" onClick={send} disabled={busy} className="rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>{busy ? "Sending…" : "Ask my parent"}</button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}

function AskMoreMoney({ onSent }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const send = async () => {
    setError("");
    const text = String(amount).trim();
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(text) || Number(text) < 1 || Number(text) > 500) return setError("Enter an amount from $1 to $500.");
    setBusy(true);
    try { await parentalApi.askFor({ kind: "money", amount: Number(text) }); setAmount(""); if (onSent) onSent(); } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ask-money">
      <label className="text-sm text-gray-700" htmlFor="ask-amount">Ask for $</label>
      <input id="ask-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" maxLength={6} disabled={busy} placeholder="10" className="w-24 rounded-lg border border-gray-300 px-2 py-1 text-sm" />
      <span className="text-sm text-gray-700">more this month</span>
      <button type="button" onClick={send} disabled={busy} className="rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>{busy ? "Sending…" : "Ask my parent"}</button>
      {error && <p className="w-full text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}

// ---- teen: the Family page section ------------------------------------------------------------------------------------------------
// canTime / canMoney: whether the teen has a daily time limit / a spending allowance (only then is there anything to ask for).
export function AskPanel({ canTime, canMoney }) {
  const [mine, setMine] = useState([]);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setMine((await parentalApi.requests())?.mine || []); } catch (e) { /* the list is optional */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const cancel = async (id) => {
    setError("");
    try { await parentalApi.cancelRequest(id); await load(); } catch (e) { setError(messageOf(e)); }
  };
  if (!canTime && !canMoney && !mine.length) return null;
  return (
    <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="teen-requests">
      <h2 className="text-base font-semibold text-gray-900">Ask your parent for more</h2>
      {canTime && <AskMoreTime onSent={load} compact />}
      {canMoney && <AskMoreMoney onSent={load} />}
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
      {mine.length > 0 && (
        <ul className="mt-4" data-testid="my-requests">
          {mine.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 py-2 text-sm">
              <span className="text-gray-800">{r.summary}<span className="ml-2 text-xs text-gray-500">{sentAt(r.created_at)}</span></span>
              <span className="flex items-center gap-3">
                <span className={`font-semibold ${STATUS_COLOR[r.status] || ""}`}>{STATUS_TEXT[r.status] || r.status}</span>
                {r.status === "pending" && <button type="button" onClick={() => cancel(r.id)} className="text-gray-500 underline" aria-label={`Cancel request for ${r.summary}`}>Cancel</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- parent: answer ---------------------------------------------------------------------------------------------------------------
export function IncomingRequests({ onAnswered }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setRows((await parentalApi.requests())?.incoming || []); } catch (e) { setRows((r) => r || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, decision) => {
    setBusyId(id); setError("");
    try { await parentalApi.decideRequest(id, decision); await load(); if (onAnswered) onAnswered(); } catch (e) { setError(messageOf(e)); await load(); } finally { setBusyId(null); }
  };
  if (!rows || !rows.length) return null;
  const open = rows.filter((r) => r.status === "pending");
  return (
    <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="incoming-requests">
      <h2 className="text-base font-semibold text-gray-900">Requests from your teens{open.length ? ` (${open.length} waiting)` : ""}</h2>
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
      <ul className="mt-3">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 py-3 last:border-0">
            <div className="min-w-0 text-sm">
              <p className="font-semibold text-gray-900">{r.child?.name} asked for {r.summary}</p>
              {r.note && <p className="text-gray-600">“{r.note}”</p>}
              <p className="text-xs text-gray-500">{sentAt(r.created_at)}</p>
            </div>
            {r.status === "pending" ? (
              <span className="flex items-center gap-3 text-sm">
                <button type="button" onClick={() => decide(r.id, "approve")} disabled={busyId === r.id} className="rounded-full px-4 py-1.5 font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }} aria-label={`Approve ${r.child?.name}: ${r.summary}`}>Approve</button>
                <button type="button" onClick={() => decide(r.id, "decline")} disabled={busyId === r.id} className="font-semibold text-red-600 underline" aria-label={`Decline ${r.child?.name}: ${r.summary}`}>Decline</button>
              </span>
            ) : <span className={`text-sm font-semibold ${STATUS_COLOR[r.status] || ""}`}>{STATUS_TEXT[r.status] === "Waiting for your parent" ? "Open" : STATUS_TEXT[r.status] || r.status}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export { money };
